import {
  GridRowId,
  GridRowSelectionModel,
  GridValidRowModel,
} from "@mui/x-data-grid-premium";
import { useCallback, useEffect, useRef, useState } from "react";

export interface UseGridSelectionProps<TRow extends GridValidRowModel> {
  /** Set grid's selected IDs. This can include IDs across pages. */
  selectedIds?: GridRowId[];
  /** Current page items */
  items: TRow[];
  /**
   * Handler called when selection changed with row data.
   *
   * **Cross-page support**: Selections ARE maintained across pages. When you select
   * rows and navigate to another page, those selections are preserved in memory.
   *
   * **Limitations**:
   * - Row data for off-page items uses cached/stale objects (not refreshed)
   * - Pre-selected IDs that aren't in the initial page won't have row data until
   *   you navigate to a page containing those items
   *
   * @param rows Array of selected row objects (may include cached data from other pages)
   */
  onSelectedRowsChanged?: (rows: TRow[]) => void;
  /** Handler called when selection changed. This can include IDs across pages */
  onSelectedIdsChanged?: (ids: GridRowId[]) => void;
  /**
   * When true, automatically deselect items that are no longer visible (filtered out).
   * When false (default), preserve selections across filter changes.
   */
  filterSelectionToVisibleItems?: boolean;
  /**
   * The current search/filter string. When provided alongside filterSelectionToVisibleItems,
   * selections are only filtered when this value changes (not on pagination).
   * This allows selections to persist across page changes while still being
   * filtered when the user changes search criteria.
   */
  searchString?: string;
}

export interface UseGridSelectionReturn<TRow extends GridValidRowModel> {
  /** Current row selection model for the grid */
  rowSelectionModel: GridRowSelectionModel;
  /** All selected rows (including across pages) */
  allSelectedRows: TRow[];
  /** Handler to update row selection model */
  setRowSelectionModel: (model: GridRowSelectionModel) => void;
  /** Count of selected items that are NOT on the current page */
  offPageSelectionCount: number;
}

// Helper function to compare arrays of IDs
const areIdsEqual = (ids1: GridRowId[], ids2: GridRowId[]): boolean => {
  if (ids1.length !== ids2.length) return false;
  const set1 = new Set(ids1);
  const set2 = new Set(ids2);
  if (set1.size !== set2.size) return false;
  for (const id of set1) {
    if (!set2.has(id)) return false;
  }
  return true;
};

// Helper function to get actual selected IDs from selection model
const getActualSelectedIds = (
  selectionModel: GridRowSelectionModel,
  currentShownItemIds: Set<GridRowId>
): GridRowId[] => {
  if (selectionModel.type === "include") {
    return Array.from(selectionModel.ids);
  } else {
    // For exclude type, return all available IDs except those in the exclude set
    return Array.from(currentShownItemIds).filter(
      (id) => !selectionModel.ids.has(id)
    );
  }
};

// Helper function to determine if an item is selected based on selection model
const isItemSelected = (
  itemId: GridRowId,
  selectionModel: GridRowSelectionModel
): boolean => {
  if (selectionModel.type === "include") {
    return selectionModel.ids.has(itemId);
  } else {
    return !selectionModel.ids.has(itemId);
  }
};

/**
 * Hook to manage grid selection state.
 *
 * TODO: Refactor the `useOmnisearchDatagrid` hook to encapsulate this hook.
 * But first, we should roll the data fetching logic into `useOmnisearchDatagrid`.
 */
export function useGridSelection<TRow extends GridValidRowModel>({
  selectedIds,
  items,
  onSelectedRowsChanged,
  onSelectedIdsChanged,
  filterSelectionToVisibleItems = false,
  searchString,
}: UseGridSelectionProps<TRow>): UseGridSelectionReturn<TRow> {
  const [rowSelectionModel, setRowSelectionModelState] =
    useState<GridRowSelectionModel>({
      type: "include",
      ids: new Set(selectedIds ?? []),
    });
  const [allSelectedRows, setAllSelectedRows] = useState<TRow[]>([]);

  // Track previous values to detect changes and prevent unnecessary callbacks
  const prevSelectedIds = useRef<GridRowId[]>([]);
  const prevAllSelectedRows = useRef<TRow[]>([]);
  const prevActualSelectedIds = useRef<GridRowId[]>([]);
  const [prevItems, setPrevItems] = useState(items);
  const prevSearchString = useRef<string | undefined>(searchString);
  // Tracks whether non-visible row objects should be preserved in allSelectedRows
  // This is set during render and read in the effect
  const shouldPreserveNonVisibleRows = useRef(true);

  if (filterSelectionToVisibleItems && items !== prevItems) {
    setPrevItems(items);

    // Determine if this is a search/filter change vs pagination change
    const searchChanged = searchString !== prevSearchString.current;
    prevSearchString.current = searchString;

    // Only filter out non-visible items when the search string has changed
    // (When search is the same, it's likely pagination - preserve selections)
    // If searchString is not provided, fall back to the original behavior (always filter)
    const shouldFilterSelections = searchString === undefined || searchChanged;

    // Preserve non-visible row objects when paginating (search didn't change)
    // Don't preserve when filtering (search changed) - those rows should be removed
    shouldPreserveNonVisibleRows.current = !shouldFilterSelections;

    if (
      shouldFilterSelections &&
      rowSelectionModel.type === "include" &&
      rowSelectionModel.ids.size > 0
    ) {
      const visibleItemIds = new Set(items.map((item) => item.id));
      const filteredIds = new Set(
        Array.from(rowSelectionModel.ids).filter((id) => visibleItemIds.has(id))
      );

      if (filteredIds.size !== rowSelectionModel.ids.size) {
        setRowSelectionModelState({ type: "include", ids: filteredIds });
      }
    }
  } else {
    // When filterSelectionToVisibleItems is false or items didn't change,
    // always preserve non-visible rows
    shouldPreserveNonVisibleRows.current = true;
  }

  // Memoized callback to update row selection model
  const setRowSelectionModel = useCallback((model: GridRowSelectionModel) => {
    setRowSelectionModelState(model);
  }, []);

  // Single reconciliation effect that handles all state synchronization
  useEffect(() => {
    let hasChanges = false;

    // Get actual selected IDs based on current selection model type
    const actualSelectedIds = getActualSelectedIds(
      rowSelectionModel,
      new Set(items.map((item) => item.id))
    );

    // 1. Sync external selectedIds to internal rowSelectionModel (if different)
    if (selectedIds && !areIdsEqual(selectedIds, prevSelectedIds.current)) {
      prevSelectedIds.current = selectedIds;
      // Only update if the actual selection is different
      if (!areIdsEqual(selectedIds, actualSelectedIds)) {
        setRowSelectionModelState({
          type: "include",
          ids: new Set(selectedIds),
        });
      }
      hasChanges = true;
    }

    // 2. Calculate new allSelectedRows based on current selection and items
    let newAllSelectedRows = allSelectedRows;

    // Get rows that are selected on current page
    const currentPageSelectedRows = items.filter((item) =>
      isItemSelected(item.id, rowSelectionModel)
    );

    if (
      rowSelectionModel.type === "include" &&
      rowSelectionModel.ids.size > 0
    ) {
      // Build allSelectedRows by prioritizing live object references from items
      const itemsMap = new Map(items.map((item) => [item.id, item]));
      const prevRowsMap = new Map(
        prevAllSelectedRows.current.map((row) => [row.id, row])
      );

      const updatedSelectedRows: TRow[] = [];

      // For each selected ID, prioritize live object from items, fallback to previous row
      for (const selectedId of rowSelectionModel.ids) {
        const liveItem = itemsMap.get(selectedId);
        if (liveItem) {
          // Use live object reference (always fresh data)
          updatedSelectedRows.push(liveItem);
        } else if (
          !filterSelectionToVisibleItems ||
          shouldPreserveNonVisibleRows.current
        ) {
          // Preserve non-visible selections when:
          // 1. filterSelectionToVisibleItems is false (cross-page selection enabled), OR
          // 2. This is pagination (search didn't change), not filtering
          const prevRow = prevRowsMap.get(selectedId);
          if (prevRow) {
            updatedSelectedRows.push(prevRow);
          }
        }
      }

      // Simple reference equality check - if we're using different object references, it changed
      const rowsChanged =
        updatedSelectedRows.length !== prevAllSelectedRows.current.length ||
        updatedSelectedRows.some(
          (row, index) => prevAllSelectedRows.current[index] !== row
        );

      if (rowsChanged) {
        newAllSelectedRows = updatedSelectedRows;
        hasChanges = true;
      }
    } else if (rowSelectionModel.type === "exclude") {
      // For exclude type: we can only track rows from current page since we don't know all possible rows
      // This is a limitation - for exclude type, allSelectedRows will only contain current page selected rows
      if (
        currentPageSelectedRows.length !== prevAllSelectedRows.current.length ||
        !currentPageSelectedRows.every(
          (row, index) => prevAllSelectedRows.current[index]?.id === row.id
        )
      ) {
        newAllSelectedRows = currentPageSelectedRows;
        hasChanges = true;
      }
    } else if (
      rowSelectionModel.ids.size === 0 &&
      prevAllSelectedRows.current.length > 0
    ) {
      // No selection
      newAllSelectedRows = [];
      hasChanges = true;
    }

    // Update state if needed
    if (hasChanges && newAllSelectedRows !== allSelectedRows) {
      setAllSelectedRows(newAllSelectedRows);
      prevAllSelectedRows.current = newAllSelectedRows;
    }

    // 3. Call callbacks only when values have actually changed

    // Call onSelectedRowsChanged if allSelectedRows changed
    if (
      hasChanges &&
      onSelectedRowsChanged &&
      newAllSelectedRows !== allSelectedRows
    ) {
      onSelectedRowsChanged(newAllSelectedRows);
    }

    // Call onSelectedIdsChanged if actual selection changed (but avoid echoing external changes)
    if (
      onSelectedIdsChanged &&
      !areIdsEqual(actualSelectedIds, prevActualSelectedIds.current)
    ) {
      prevActualSelectedIds.current = actualSelectedIds;

      // Only notify if this change didn't originate from external selectedIds
      const isExternalChange =
        selectedIds &&
        areIdsEqual(actualSelectedIds, selectedIds) &&
        areIdsEqual(selectedIds, prevSelectedIds.current);

      if (!isExternalChange) {
        onSelectedIdsChanged(actualSelectedIds);
      }
    }
  }, [
    selectedIds,
    items,
    rowSelectionModel,
    allSelectedRows,
    onSelectedRowsChanged,
    onSelectedIdsChanged,
    filterSelectionToVisibleItems,
    searchString,
  ]);

  // Calculate count of selected items not on current page
  const offPageSelectionCount = (() => {
    if (rowSelectionModel.type !== "include" || rowSelectionModel.ids.size === 0) {
      return 0;
    }
    const currentPageIds = new Set(items.map((item) => item.id));
    let count = 0;
    for (const id of rowSelectionModel.ids) {
      if (!currentPageIds.has(id)) {
        count++;
      }
    }
    return count;
  })();

  return {
    rowSelectionModel,
    allSelectedRows,
    setRowSelectionModel,
    offPageSelectionCount,
  };
}
